import type { RumEvent } from '@datadog/browser-rum';
import type { LogsEvent } from '@datadog/browser-logs';

const PHI_KEYS = [
  'mrn',
  'dob',
  'patientName',
  'patientId',
  'patient_id',
  'patientKey',
  'patient_key',
  'accession',
  'accessionNumber',
  'accession_number',
  'phone',
  'email',
  'studyKey',
  'study_key',
  'orderKey',
  'order_key',
  'sessionToken',
  'session_token',
  'authToken',
  'auth_token',
  'token',
  'shareGuid',
  'share_guid',
  'guid',
];

const GUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const LONG_HEX_SEGMENT = /^[0-9a-f]{16,}$/i;
const JWT_SEGMENT = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+)?$/;
const OPAQUE_SEGMENT = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z0-9._~-]{20,}$/;
const URL_OR_PATH = /(https?:\/\/[^\s"'<>]+|\/[^\s"'<>]+)/g;

const EXAM_SHARE_STATIC_SEGMENTS = new Set([
  'links',
  'generate',
  'validateaccess',
  'myexams',
  'myreport',
]);

// Returns the replacement label, or null if the segment is not sensitive.
function isSensitiveRouteParam(segment: string, previousSegment: string | undefined): ':token' | null {
  const previous = previousSegment?.toLowerCase();
  const current = segment.toLowerCase();

  if (previous === 'share' || previous === 'engage') return ':token';
  if (previous === 'examshare' && !EXAM_SHARE_STATIC_SEGMENTS.has(current)) return ':token';

  return null;
}

function scrubPathSegment(segment: string, previousSegment: string | undefined): string {
  if (!segment) return segment;

  let decoded = segment;
  try {
    decoded = decodeURIComponent(segment);
  } catch {
    // Keep the raw segment when it is not valid percent-encoding.
  }

  if (/^\d+$/.test(decoded)) return ':id';
  if (GUID_SEGMENT.test(decoded)) return ':guid';
  if (JWT_SEGMENT.test(decoded)) return ':token';
  if (LONG_HEX_SEGMENT.test(decoded)) return ':token';
  if (OPAQUE_SEGMENT.test(decoded)) return ':token';
  const contextLabel = isSensitiveRouteParam(decoded, previousSegment);
  if (contextLabel) return contextLabel;

  // Known gap: short alphanumeric IDs that are not purely numeric (e.g. "ABC123",
  // "MRN-456") pass through unredacted. All current PortalApi routes use :int
  // constraints so this does not occur today. If non-numeric path params are
  // introduced, revisit isSensitiveRouteParam with an entity-prefix allowlist
  // that also whitelists static sub-paths (e.g. AppointmentSummary, Profile).
  return segment;
}

function scrubPath(pathname: string): string {
  const segments = pathname.split('/');
  let previousMeaningfulSegment: string | undefined;

  return segments
    .map((segment) => {
      const scrubbed = scrubPathSegment(segment, previousMeaningfulSegment);
      if (segment) previousMeaningfulSegment = segment;
      return scrubbed;
    })
    .join('/');
}

function scrubUrlCandidate(value: string): string {
  const isAbsolute = /^https?:\/\//i.test(value);
  const isPath = value.startsWith('/');
  if (!isAbsolute && !isPath) return value;

  try {
    const parsed = new URL(value, 'http://telemetry.local');
    parsed.pathname = scrubPath(parsed.pathname);
    parsed.search = '';
    parsed.hash = '';
    return isAbsolute ? parsed.href : parsed.pathname;
  } catch {
    return value;
  }
}

export function scrubUrlForTelemetry(value: string): string {
  const direct = scrubUrlCandidate(value);
  if (direct !== value) return direct;

  return value.replace(URL_OR_PATH, (candidate) => scrubUrlCandidate(candidate));
}

function scrubViewUrl(event: { view?: { url?: string; referrer?: string; name?: string } }): void {
  if (event.view?.url) event.view.url = scrubUrlForTelemetry(event.view.url);
  if (event.view?.referrer) event.view.referrer = scrubUrlForTelemetry(event.view.referrer);
  if (event.view?.name) event.view.name = scrubUrlForTelemetry(event.view.name);
}

function scrubContext(e: Record<string, unknown>): void {
  const ctx = e['context'] as Record<string, unknown> | undefined;
  PHI_KEYS.forEach(k => delete ctx?.[k]);
}

function scrubError(e: Record<string, unknown>): void {
  const error = e['error'] as Record<string, unknown> | undefined;
  if (typeof error?.['message'] === 'string') error['message'] = scrubUrlForTelemetry(error['message']);
  if (typeof error?.['stack'] === 'string') error['stack'] = scrubUrlForTelemetry(error['stack']);
}

export function rumBeforeSend(event: RumEvent): boolean {
  scrubViewUrl(event);

  const e = event as Record<string, unknown>;

  const resource = e['resource'] as Record<string, unknown> | undefined;
  if (typeof resource?.['url'] === 'string') resource['url'] = scrubUrlForTelemetry(resource['url']);

  scrubError(e);
  scrubContext(e);
  return true;
}

export function logsBeforeSend(event: LogsEvent): boolean {
  scrubViewUrl(event);

  if (typeof event.message === 'string') event.message = scrubUrlForTelemetry(event.message);

  const le = event as Record<string, unknown>;
  scrubError(le);
  scrubContext(le);
  return true;
}
